import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, InfoWindow } from '@react-google-maps/api';
import AdvancedMarker from '../common/AdvancedMarker';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Collapse
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as AvailableIcon,
  DoNotDisturb as BusyIcon,
  Cancel as OfflineIcon,
  Person as DriversIcon,
  Route as RidesIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MyLocation as MyLocationIcon,
  LocalTaxi as TaxiIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import LocationService from '../../utils/locationService';

// Konstanten außerhalb der Komponente für stabile Referenzen
const mapContainerStyle = {
  width: '100%',
  height: '700px',
  margin: 0,
  padding: 0
};

const defaultCenter = {
  lat: 50.1109,
  lng: 8.6821
};

const defaultOptions = {
  fullscreenControl: true,
  streetViewControl: true,
  mapTypeControl: true,
  zoomControl: true,
  minZoom: 3,
  maxZoom: 18,
  mapTypeControlOptions: {
    position: 2, // TOP_LEFT — wird durch das Panel verdeckt, daher unten anpassen
    style: 1
  },
  streetViewControlOptions: {
    position: 3
  },
  fullscreenControlOptions: {
    position: 7
  },
  mapId: process.env.REACT_APP_GOOGLE_MAPS_ID || '8bb9c199f544a76'
};


const STATUS_LABELS = {
  'available':     'Verfügbar',
  'en_route':      'Auf dem Weg',
  'with_customer': 'Mit Kunde',
  'paused':        'Pause',
  'offline':       'Offline',
};

// Status aus isOnline/isAvailable + aktiver Fahrt ableiten
// activeRideStatus: der Status der aktiven Fahrt des Fahrers (oder null)
const deriveDriverStatus = (driver, activeRideStatus) => {
  if (!driver.isOnline) return 'offline';
  if (driver.isAvailable) return 'available';
  // Online aber nicht verfügbar → aus Fahrt-Status ableiten
  if (activeRideStatus === 'in_progress') return 'with_customer';
  if (activeRideStatus === 'accepted' || activeRideStatus === 'arrived') return 'en_route';
  // Online, nicht verfügbar, keine aktive Fahrt → Pause
  return 'paused';
};

const StatusChip = styled(Chip)(({ theme, status }) => {
  const colorMap = {
    available: 'success',
    en_route: 'warning',
    with_customer: 'error',
    paused: 'info',
  };
  const color = colorMap[status] || 'default';

  return {
    color: color === 'default' ? 'inherit' : 'white',
    backgroundColor: color === 'default' ? undefined : theme.palette[color]?.main,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

// Status-Dot für die Fahrerliste
const StatusDot = styled(Box)(({ theme, status }) => {
  const colorMap = {
    available: theme.palette.success.main,
    en_route: theme.palette.warning.main,
    with_customer: theme.palette.error.main,
    paused: theme.palette.info.main,
    offline: theme.palette.grey[500],
  };
  return {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: colorMap[status] || theme.palette.grey[500],
    flexShrink: 0,
  };
});

const LiveMap = React.memo(() => {
  // Daten aus AppDataManager (bereits Echtzeit via onSnapshot)
  const drivers = useSelector(state => state.appData.drivers);
  const rides = useSelector(state => state.appData.rides);
  const driversLoading = useSelector(state => state.appData.isLoading.drivers);
  const driversError = useSelector(state => state.appData.error.drivers);

  const [map, setMap] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [driverLocations, setDriverLocations] = useState({});
  const [searchText, setSearchText] = useState('');
  const [driverListOpen, setDriverListOpen] = useState(true);

  const unsubscribeRef = useRef(null);

  // Google Maps API laden
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: window.GOOGLE_MAPS_LIBRARIES,
    version: 'weekly'
  });

  // Echtzeit-Standort-Listener (immer alle Fahrer — Filter passiert in der UI)
  useEffect(() => {
    const unsubscribe = LocationService.subscribeToAllDriverLocations((locations) => {
      setDriverLocations(locations);
    });
    unsubscribeRef.current = unsubscribe;

    return () => {
      if (typeof unsubscribeRef.current === 'function') {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  // Map: driverId → aktiver Fahrtstatus (accepted, arrived, in_progress)
  const activeRideByDriver = useMemo(() => {
    const map = {};
    const activeStatuses = ['accepted', 'arrived', 'in_progress'];
    rides.forEach(ride => {
      if (ride.driverId && activeStatuses.includes(ride.status)) {
        // Bei mehreren aktiven Fahrten gewinnt die "höchste" (in_progress > arrived > accepted)
        const priority = { in_progress: 3, arrived: 2, accepted: 1 };
        if (!map[ride.driverId] || priority[ride.status] > (priority[map[ride.driverId]] || 0)) {
          map[ride.driverId] = ride.status;
        }
      }
    });
    return map;
  }, [rides]);

  // Fahrer mit abgeleitetem Status und Position anreichern
  const enrichedDrivers = useMemo(() => {
    return drivers.map(driver => {
      const activeRideStatus = activeRideByDriver[driver.id] || null;
      const status = deriveDriverStatus(driver, activeRideStatus);
      const realtimeLocation = driverLocations[driver.id];
      const hasLocation = realtimeLocation ||
                         (driver.location && driver.location.latitude && driver.location.longitude);

      const position = realtimeLocation
        ? { lat: realtimeLocation.latitude, lng: realtimeLocation.longitude }
        : driver.location
          ? { lat: driver.location.latitude, lng: driver.location.longitude }
          : null;

      const displayName = driver.name || driver.user?.name || 'Unbekannter Fahrer';
      // Initialen für den Marker (z.B. "Max Mustermann" → "MM")
      const nameParts = displayName.split(' ').filter(Boolean);
      const initials = nameParts.length >= 2
        ? (nameParts[0][0] + nameParts[nameParts.length - 1][0])
        : displayName.substring(0, 2);

      return {
        ...driver,
        derivedStatus: status,
        hasLocation,
        position,
        isRealtime: !!realtimeLocation,
        displayName,
        initials: initials.toUpperCase(),
        vehicleInfo: driver.car
          ? `${driver.car.model || ''} ${driver.car.licensePlate ? `(${driver.car.licensePlate})` : ''}`.trim()
          : null,
      };
    });
  }, [drivers, driverLocations]);

  // Status-Sortierpriorität: Verfügbar > Besetzt (en_route, with_customer) > Pause > Offline
  const STATUS_SORT_PRIORITY = { available: 4, en_route: 3, with_customer: 2, paused: 1, offline: 0 };

  // Gefilterte und sortierte Fahrer
  const filteredDrivers = useMemo(() => {
    return enrichedDrivers.filter(driver => {
      // Statusfilter
      if (filterStatus !== 'all' && driver.derivedStatus !== filterStatus) return false;
      // Suchfilter
      if (searchText) {
        const search = searchText.toLowerCase();
        const nameMatch = driver.displayName.toLowerCase().includes(search);
        const vehicleMatch = driver.vehicleInfo?.toLowerCase().includes(search);
        const phoneMatch = driver.phone?.toLowerCase().includes(search);
        if (!nameMatch && !vehicleMatch && !phoneMatch) return false;
      }
      return true;
    }).sort((a, b) => {
      const priorityDiff = (STATUS_SORT_PRIORITY[b.derivedStatus] ?? 0) - (STATUS_SORT_PRIORITY[a.derivedStatus] ?? 0);
      if (priorityDiff !== 0) return priorityDiff;
      return (a.displayName || '').localeCompare(b.displayName || '');
    });
  }, [enrichedDrivers, filterStatus, searchText]);

  // Fahrer mit Standort für die Karte
  const driversWithLocation = useMemo(() => {
    return filteredDrivers.filter(d => d.hasLocation);
  }, [filteredDrivers]);

  // Status-Zusammenfassung für Chips
  const statusCounts = useMemo(() => {
    const counts = { all: enrichedDrivers.length, available: 0, en_route: 0, with_customer: 0, paused: 0, offline: 0 };
    enrichedDrivers.forEach(d => {
      if (counts[d.derivedStatus] !== undefined) {
        counts[d.derivedStatus]++;
      }
    });
    return counts;
  }, [enrichedDrivers]);

  // Kartengrenzen anpassen
  const fitBounds = useCallback(() => {
    if (!map || driversWithLocation.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    driversWithLocation.forEach(driver => {
      bounds.extend(driver.position);
    });

    map.fitBounds(bounds);
    if (driversWithLocation.length === 1) {
      setTimeout(() => map.setZoom(15), 100);
    }
  }, [map, driversWithLocation]);

  // Grenzen anpassen bei Filteränderung
  useEffect(() => {
    if (map && driversWithLocation.length > 0) {
      fitBounds();
    }
  }, [map, filterStatus, fitBounds]);

  // Fahrer auf Karte fokussieren
  const focusDriver = useCallback((driver) => {
    if (!map || !driver.position) return;
    map.panTo(driver.position);
    map.setZoom(16);
    setSelectedDriver(driver);
  }, [map]);

  // InfoWindow-Position: Echtzeit-Position verwenden wenn vorhanden
  const getInfoWindowPosition = useCallback((driver) => {
    if (!driver) return null;
    // Verwende die angereicherte Position (bereits Echtzeit-berücksichtigt)
    return driver.position;
  }, []);

  // Lade-Fehler
  if (loadError) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Google Maps API: {loadError.message}
      </Alert>
    );
  }

  // Ladezustand
  if (!isLoaded || (driversLoading && !drivers.length)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {!isLoaded ? 'Karte wird geladen...' : 'Fahrerdaten werden geladen...'}
        </Typography>
      </Box>
    );
  }

  // Fehler
  if (driversError) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Fahrerdaten: {typeof driversError === 'object' ? (driversError.message || JSON.stringify(driversError)) : driversError}
      </Alert>
    );
  }

  return (
    <Card elevation={3} className="dashboard-live-map" sx={{ borderRadius: 0, width: '100%', maxWidth: '100%' }}>
      <CardContent sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            Live-Fahrerstandorte
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={fitBounds}
              title="Alle Fahrer anzeigen"
              sx={{
                bgcolor: 'primary.light',
                '&:hover': { bgcolor: 'primary.main', color: 'white' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Statusfilter-Chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', px: 2 }}>
          <Chip
            label={`Alle (${statusCounts.all})`}
            variant={filterStatus === 'all' ? 'filled' : 'outlined'}
            color={filterStatus === 'all' ? 'primary' : 'default'}
            onClick={() => setFilterStatus('all')}
          />
          <StatusChip
            label={`Verfügbar (${statusCounts.available})`}
            status="available"
            variant={filterStatus === 'available' ? 'filled' : 'outlined'}
            onClick={() => setFilterStatus('available')}
          />
          <StatusChip
            label={`Auf dem Weg (${statusCounts.en_route})`}
            status="en_route"
            variant={filterStatus === 'en_route' ? 'filled' : 'outlined'}
            onClick={() => setFilterStatus('en_route')}
          />
          <StatusChip
            label={`Mit Kunde (${statusCounts.with_customer})`}
            status="with_customer"
            variant={filterStatus === 'with_customer' ? 'filled' : 'outlined'}
            onClick={() => setFilterStatus('with_customer')}
          />
          <StatusChip
            label={`Pause (${statusCounts.paused})`}
            status="paused"
            variant={filterStatus === 'paused' ? 'filled' : 'outlined'}
            onClick={() => setFilterStatus('paused')}
          />
          <StatusChip
            label={`Offline (${statusCounts.offline})`}
            status="offline"
            variant={filterStatus === 'offline' ? 'filled' : 'outlined'}
            onClick={() => setFilterStatus('offline')}
          />
        </Box>

        {/* Karte mit Fahrerliste-Overlay */}
        <Box sx={{ position: 'relative' }}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={defaultCenter}
            zoom={12}
            options={defaultOptions}
            onLoad={onMapLoad}
            mapContainerClassName="google-map-container"
          >
            {/* Fahrer-Marker */}
            {driversWithLocation.map(driver => (
              <AdvancedMarker
                key={driver.id}
                position={driver.position}
                status={driver.derivedStatus}
                label={driver.initials}
                isRealtime={driver.isRealtime}
                onClick={() => setSelectedDriver(driver)}
                title={`${driver.displayName}${driver.isRealtime ? ' (Live)' : ''}`}
              />
            ))}

            {/* Info-Fenster */}
            {selectedDriver && selectedDriver.position && (
              <InfoWindow
                position={getInfoWindowPosition(selectedDriver)}
                onCloseClick={() => setSelectedDriver(null)}
              >
                <Box sx={{ minWidth: 200, maxWidth: 300 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {selectedDriver.displayName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1 }}>
                    <StatusChip
                      label={STATUS_LABELS[selectedDriver.derivedStatus] || selectedDriver.derivedStatus}
                      status={selectedDriver.derivedStatus}
                      size="small"
                    />
                    {selectedDriver.isRealtime && (
                      <Chip label="Live" size="small" color="info" sx={{ ml: 1 }} />
                    )}
                  </Box>
                  {selectedDriver.phone && (
                    <Typography variant="body2">
                      Telefon: {selectedDriver.phone}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    Bewertung: {selectedDriver.rating || 'Keine'} ({selectedDriver.ratingCount || 0} Bewertungen)
                  </Typography>
                  <Typography variant="body2">
                    Fahrzeug: {selectedDriver.vehicleInfo || 'Kein Fahrzeug zugewiesen'}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    component={Link}
                    to={`/fahrer/${selectedDriver.id}`}
                    sx={{ mt: 1 }}
                    fullWidth
                  >
                    Profil anzeigen
                  </Button>
                </Box>
              </InfoWindow>
            )}
          </GoogleMap>

          {/* Fahrerliste-Overlay (links oben auf der Karte) */}
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              width: 320,
              maxHeight: 'calc(100% - 20px)',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 2,
              boxShadow: 3,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              zIndex: 1,
            }}
          >
            {/* Header mit Toggle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 1,
                borderBottom: driverListOpen ? '1px solid' : 'none',
                borderColor: 'divider',
                cursor: 'pointer',
              }}
              onClick={() => setDriverListOpen(!driverListOpen)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DriversIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Fahrer ({filteredDrivers.length})
                </Typography>
              </Box>
              <IconButton size="small">
                {driverListOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={driverListOpen}>
              {/* Suchfeld */}
              <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Fahrer suchen..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'white',
                      fontSize: '0.85rem',
                    }
                  }}
                />
              </Box>

              {/* Fahrerliste */}
              <Box
                sx={{
                  overflowY: 'auto',
                  maxHeight: 450,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'grey.400',
                    borderRadius: 3,
                  },
                }}
              >
                {filteredDrivers.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Keine Fahrer gefunden
                    </Typography>
                  </Box>
                ) : (
                  filteredDrivers.map(driver => (
                    <Box
                      key={driver.id}
                      onClick={() => focusDriver(driver)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        cursor: driver.hasLocation ? 'pointer' : 'default',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        opacity: driver.hasLocation ? 1 : 0.5,
                        bgcolor: selectedDriver?.id === driver.id ? 'action.selected' : 'transparent',
                        '&:hover': {
                          bgcolor: driver.hasLocation ? 'action.hover' : 'transparent',
                        },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      {/* Status-Punkt */}
                      <StatusDot status={driver.derivedStatus} />

                      {/* Fahrer-Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {driver.displayName}
                          </Typography>
                          {driver.isRealtime && (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: 'info.main',
                                flexShrink: 0,
                              }}
                              title="Live-Position"
                            />
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {driver.vehicleInfo || 'Kein Fahrzeug'}
                          {driver.phone ? ` · ${driver.phone}` : ''}
                        </Typography>
                      </Box>

                      {/* Status-Label */}
                      <StatusChip
                        label={STATUS_LABELS[driver.derivedStatus] || driver.derivedStatus}
                        status={driver.derivedStatus}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          component={Link}
          to="/fahrer"
          size="medium"
          variant="outlined"
          startIcon={<DriversIcon />}
          sx={{ fontWeight: 'bold' }}
        >
          Alle Fahrer anzeigen
        </Button>
        <Button
          component={Link}
          to="/fahrten"
          size="medium"
          variant="contained"
          startIcon={<RidesIcon />}
          sx={{ fontWeight: 'bold' }}
        >
          Aktive Fahrten anzeigen
        </Button>
      </CardActions>
    </Card>
  );
});

export default LiveMap;
